import { ChangeEvent, FC } from 'react';
import { ArrowLeft, PageHero, Typography, Button, Info } from '@dreamteam-monorepo/ui';
import { BELeague } from '@/lib/leagueMappers';
import { JoinLeagueCard } from '@/components/JoinLeagueCard/JoinLeagueCard';

export interface JoinLeaguePageHeroProps {
  pin: string;
  onPinChange: (value: string) => void;
  onFindLeague: () => void;
  onCreateLeague?: () => void;
  onBack: () => void;
  findLoading?: boolean;
  notFound?: boolean;
  foundLeague?: BELeague | null;
  onJoinFoundLeague?: () => void;
  joiningFound?: boolean;
  className?: string;
}

export const JoinLeaguePageHero: FC<JoinLeaguePageHeroProps> = ({
  pin,
  onPinChange,
  onFindLeague,
  onCreateLeague,
  onBack,
  findLoading = false,
  notFound = false,
  foundLeague,
  onJoinFoundLeague,
  joiningFound = false,
  className,
}) => {
  const isActive = pin.trim().length > 0;

  return (
    <PageHero className={className}>
      <div className="flex flex-col gap-[--padding-lg]">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 self-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded-[--corner-xs]"
        >
          <ArrowLeft className="size-6 text-[--color-blue-200]" />
          <Typography as="span" variant="button020" className="text-[--color-blue-200]">
            Back
          </Typography>
        </button>

        <div className="flex flex-col gap-[--padding-lg]">
          <Typography
            as="h1"
            variant="headline050"
            className="text-[--page-header-header] !font-[900] sm:text-40 sm:leading-44"
          >
            JOIN A LEAGUE
          </Typography>

          <div className="flex flex-col gap-[--padding-xs]">
            <div className="flex flex-col gap-[--padding-sm] sm:flex-row sm:items-end sm:gap-[--padding-lg]">
              <div className="flex flex-col gap-[--padding-xs] flex-1">
                <Typography variant="paragraph010" className="text-[--page-header-header]">
                  Enter League Pin
                </Typography>
                <div
                  className={[
                    'h-[48px] px-[--padding-md] rounded-[--corner-xs] border border-solid',
                    'bg-[--color-grey-100] flex items-center',
                    isActive ? 'border-[#201d4a]' : 'border-[--color-grey-100]',
                  ].join(' ')}
                >
                  <input
                    type="text"
                    value={pin}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onPinChange(e.target.value)}
                    placeholder="eg. X123456X"
                    className="flex-1 bg-transparent outline-none font-primary text-14 leading-20 text-[--color-neutral-900] placeholder:text-[--color-neutral-600]"
                  />
                </div>
              </div>

              <div className="flex gap-[--padding-sm] shrink-0">
                <Button
                  variant="contained"
                  color="blue"
                  disabled={!isActive || findLoading}
                  loading={findLoading}
                  onClick={onFindLeague}
                  className="flex-1 sm:flex-none sm:w-[160px]"
                >
                  Find League
                </Button>
                {onCreateLeague && (
                  <Button
                    variant="outlined"
                    color="blue"
                    onClick={onCreateLeague}
                    className="flex-1 sm:flex-none sm:w-[160px]"
                  >
                    Create League
                  </Button>
                )}
              </div>
            </div>

            {notFound && (
              <div className="flex items-center gap-[--padding-xs]">
                <Info className="size-6 shrink-0 text-[--page-header-header]" />
                <Typography variant="subheadline020" className="text-[--page-header-header]">
                  No league was found
                </Typography>
              </div>
            )}
          </div>

          {foundLeague && (
            <div className="flex flex-col gap-[--padding-md]">
              <Typography variant="subheadline020" className="text-[--page-header-header]">
                1 league found
              </Typography>
              <JoinLeagueCard
                league={foundLeague}
                onJoin={onJoinFoundLeague ?? (() => {})}
                joining={joiningFound}
                defaultExpanded
              />
            </div>
          )}
        </div>
      </div>
    </PageHero>
  );
};
